// lib/generationAccept.ts
//
// Persist whether the user kept or rejected a generated result. Callers may keep
// using this fire-and-forget; List feedback reads the returned status so an old
// output with no snapshot can warn without breaking the card.

export type GenerationAcceptStatus = "ok" | "missing" | "error";
export type GenerationFeedbackValue = "good" | "bad";
export type ProjectOutputSignals = {
  favoriteIds: string[];
  feedbackByOutputId: Record<string, GenerationFeedbackValue>;
};

const isProjectOutputFeedback = (value: unknown): value is GenerationFeedbackValue => value === 'good' || value === 'bad';

export async function markGenerationTaskAccepted(
  versionId: string,
  accepted = true,
  options?: { factoryFeedback?: GenerationFeedbackValue | "clear" },
): Promise<GenerationAcceptStatus> {
  return markGenerationTaskAcceptedAt('/api/generation-tasks/by-version/', versionId, accepted, undefined, options);
}

export async function markGenerationTaskAcceptedForLayer(
  layerId: string,
  projectId: string,
  accepted = true,
  options?: { factoryFeedback?: GenerationFeedbackValue | "clear"; outputFeedback?: GenerationFeedbackValue },
): Promise<GenerationAcceptStatus> {
  return markGenerationTaskAcceptedAt('/api/generation-tasks/by-layer/', layerId, accepted, projectId, options);
}

async function markGenerationTaskAcceptedAt(
  endpoint: string,
  outputId: string,
  accepted: boolean,
  projectId: string | undefined,
  options?: { factoryFeedback?: GenerationFeedbackValue | "clear"; outputFeedback?: GenerationFeedbackValue },
): Promise<GenerationAcceptStatus> {
  if (!outputId) return "missing";
  try {
    const response = await fetch(endpoint + encodeURIComponent(outputId), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        accepted,
        ...(projectId ? { projectId } : {}),
        ...(options?.factoryFeedback ? { factoryFeedback: options.factoryFeedback } : {}),
        ...(options?.outputFeedback ? { outputFeedback: options.outputFeedback } : {}),
      }),
    });
    if (response.status === 404) return "missing";
    if (!response.ok) return "error";
    return "ok";
  } catch (error) {
    console.error("[generationAccept] Failed to persist accept signal", error);
    return "error";
  }
}

export const loadProjectOutputSignals = async (
  projectId: string,
  userId?: string | null,
): Promise<ProjectOutputSignals> => {
  const empty: ProjectOutputSignals = { favoriteIds: [], feedbackByOutputId: {} };
  const normalizedProjectId = projectId.trim();
  const normalizedUserId = typeof userId === 'string' ? userId.trim() : '';
  if (!normalizedProjectId || !normalizedUserId || typeof window === 'undefined') return empty;

  try {
    const response = await fetch(`/api/inventory/projects/${encodeURIComponent(normalizedProjectId)}/snapshots`);
    if (!response.ok) return empty;
    const data = await response.json() as { snapshots?: unknown; projectOutputSignals?: unknown };
    const snapshots = Array.isArray(data.snapshots) ? data.snapshots : [];
    const favoriteIds = new Set<string>();
    const feedbackByOutputId: Record<string, GenerationFeedbackValue> = {};

    for (const snapshot of snapshots) {
      if (!snapshot || typeof snapshot !== 'object') continue;
      const record = snapshot as Record<string, unknown>;
      if (typeof record.projectId === 'string' && record.projectId !== normalizedProjectId) continue;
      if (normalizedUserId && record.userId !== normalizedUserId) continue;
      const annotations = Array.isArray(record.annotations) ? record.annotations : [];
      for (const annotation of annotations) {
        if (!annotation || typeof annotation !== 'object') continue;
        const signal = annotation as Record<string, unknown>;
        if (normalizedUserId && signal.userId !== normalizedUserId) continue;
        const outputId = typeof signal.layerId === 'string' ? signal.layerId.trim() : '';
        if (!outputId) continue;
        if (signal.kind === 'project-favorite') {
          if (signal.value === 'favorite') favoriteIds.add(outputId);
          else if (signal.value === 'not-favorite') favoriteIds.delete(outputId);
        } else if (signal.kind === 'project-feedback' && isProjectOutputFeedback(signal.value)) {
          feedbackByOutputId[outputId] = signal.value;
        }
      }
    }

    if (data.projectOutputSignals && typeof data.projectOutputSignals === 'object' && !Array.isArray(data.projectOutputSignals)) {
      for (const [outputId, value] of Object.entries(data.projectOutputSignals)) {
        if (!outputId || !value || typeof value !== 'object' || Array.isArray(value)) continue;
        const signal = value as Record<string, unknown>;
        if (signal.favorite === true) favoriteIds.add(outputId);
        else if (signal.favorite === false) favoriteIds.delete(outputId);
        if (isProjectOutputFeedback(signal.feedback)) feedbackByOutputId[outputId] = signal.feedback;
      }
    }

    return { favoriteIds: [...favoriteIds], feedbackByOutputId };
  } catch {
    return empty;
  }
};
